import { useMemo } from "react";
import { useReportHistoryByUID } from "./useReportHistory";
import { isQuestionAnswered } from "../utils/optionalDataUtils";

/**
 * Hook to calculate compliance trends for a client
 * Returns trend analysis based on historical compliance data
 *
 * @param {string} uid - Client user ID
 * @param {string} organizationId - Organization ID
 * @param {number} limit - Maximum number of reports to analyze (default: 10)
 * @returns {Object} Trend analysis object
 */
const useReportComplianceTrend = (uid, organizationId, limit = 10) => {
  const { reports, loading, error } = useReportHistoryByUID(
    uid,
    organizationId,
    limit,
  );

  const trendAnalysis = useMemo(() => {
    if (loading) {
      return {
        trend: "loading",
        message: "Loading compliance data...",
        confidence: 0,
        dataPoints: 0,
        changePercentage: 0,
        currentScore: 0,
        previousScore: 0,
      };
    }

    if (error) {
      return {
        trend: "error",
        message: error,
        confidence: 0,
        dataPoints: 0,
        changePercentage: 0,
        currentScore: 0,
        previousScore: 0,
      };
    }

    if (!reports || reports.length === 0) {
      return {
        trend: "no_data",
        message: "No compliance data available",
        confidence: 0,
        dataPoints: 0,
        changePercentage: 0,
        currentScore: 0,
        previousScore: 0,
      };
    }

    // Sort reports by creation date (most recent first)
    const sortedReports = [...reports].sort(
      (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
    );

    // Calculate compliance scores for each report using the actual useReportCompliance logic
    const complianceScores = sortedReports
      .map((report, index) => {
        const compliance = calculateReportCompliance(report);
        return {
          reportId: report.id || index,
          createdOn: report.createdOn,
          score: compliance.overallScore,
          rating: compliance.overallRating,
          hasValidData: compliance.overallScore > 0,
        };
      })
      .filter((item) => item.hasValidData); // Filter out reports with no valid compliance data

    const dataPoints = complianceScores.length;

    // Check for minimum statistical validity
    if (dataPoints < 2) {
      return {
        trend: "insufficient_data",
        message:
          "Not enough data points for trend analysis (minimum 2 reports required)",
        confidence: 0,
        dataPoints,
        changePercentage: 0,
        currentScore: dataPoints > 0 ? complianceScores[0].score : 0,
        previousScore: 0,
      };
    }

    // Get the most recent and previous scores for comparison
    const currentScore = complianceScores[0].score;
    const previousScore = complianceScores[1].score;

    // Calculate percentage change
    const changePercentage =
      previousScore > 0
        ? ((currentScore - previousScore) / previousScore) * 100
        : 0;

    // Calculate statistical confidence based on data points and consistency
    const confidence = calculateStatisticalConfidence(complianceScores);

    // Determine trend direction with smoothing threshold
    const smoothingThreshold = 5; // 5% threshold to smooth out minor fluctuations
    let trend;
    let message;

    if (confidence < 50) {
      trend = "insufficient_data";
      message = `Limited data confidence (${dataPoints} reports, ${confidence}% confidence). More data needed for reliable trend analysis.`;
    } else if (Math.abs(changePercentage) <= smoothingThreshold) {
      trend = "stable";
      message = `Compliance is stable (${changePercentage >= 0 ? "+" : ""}${changePercentage.toFixed(1)}% change)`;
    } else if (changePercentage > smoothingThreshold) {
      trend = "improving";
      message = `Compliance is improving (+${changePercentage.toFixed(1)}% change)`;
    } else {
      trend = "declining";
      message = `Compliance is declining (${changePercentage.toFixed(1)}% change)`;
    }

    // Enhanced trend analysis for longer-term patterns
    if (dataPoints >= 4 && confidence >= 70) {
      const longerTermTrend = calculateLongerTermTrend(
        complianceScores.slice(0, Math.min(6, dataPoints)),
      );
      if (
        longerTermTrend.direction !== trend &&
        Math.abs(longerTermTrend.slope) > 1
      ) {
        message += ` (${longerTermTrend.direction} long-term trend over ${longerTermTrend.periods} reports)`;
      }
    }

    return {
      trend,
      message,
      confidence,
      dataPoints,
      changePercentage: Math.round(changePercentage * 10) / 10,
      currentScore,
      previousScore,
      scores: complianceScores,
      statisticalValidity: confidence >= 50 ? "valid" : "insufficient",
    };
  }, [reports, loading, error, limit]);

  return trendAnalysis;
};

/**
 * Calculate statistical confidence based on data points and consistency
 */
const calculateStatisticalConfidence = (complianceScores) => {
  const dataPoints = complianceScores.length;

  // Base confidence on number of data points
  let confidence = 0;
  if (dataPoints >= 2) confidence = 30;
  if (dataPoints >= 3) confidence = 50;
  if (dataPoints >= 5) confidence = 70;
  if (dataPoints >= 8) confidence = 90;

  // Adjust confidence based on data consistency
  if (dataPoints >= 3) {
    const recentScores = complianceScores.slice(0, Math.min(5, dataPoints));
    const variance = calculateVariance(recentScores.map((item) => item.score));
    const standardDeviation = Math.sqrt(variance);

    // Lower confidence if data is too volatile (high standard deviation)
    if (standardDeviation > 20) confidence *= 0.7;
    else if (standardDeviation > 15) confidence *= 0.8;
    else if (standardDeviation > 10) confidence *= 0.9;
  }

  return Math.round(confidence);
};

/**
 * Calculate compliance score for a single report
 * This mirrors the logic from useReportCompliance but without React hooks
 */
const calculateReportCompliance = (report) => {
  if (!report) {
    return { overallScore: 0, overallRating: 0 };
  }

  // Convert Firebase timestamps to dates
  let startDate, endDate;
  try {
    startDate = report.startDate?.seconds
      ? new Date(report.startDate.seconds * 1000)
      : new Date();
    endDate = report.endDate?.seconds
      ? new Date(report.endDate.seconds * 1000)
      : new Date();
  } catch (e) {
    startDate = new Date();
    endDate = new Date();
  }

  const totalDays =
    Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) || 1;

  let totalScore = 0;
  let totalWeight = 0;

  // Exercise compliance (25% weight)
  const exerciseData = report.exerciseData || [];
  if (exerciseData.length > 0) {
    const completedWorkouts = exerciseData.filter(
      (w) => w.status === "complete",
    ).length;
    const exerciseScore = (completedWorkouts / exerciseData.length) * 100;
    totalScore += exerciseScore * 0.25;
    totalWeight += 0.25;
  }

  // Nutrition compliance (30% weight)
  const healthData = report.healthData || [];
  if (healthData.length > 0) {
    let daysWithMacros = 0;
    let daysWithWater = 0;
    let totalGoalAdherence = 0;
    let goalAdherenceDays = 0;

    healthData.forEach((day) => {
      const data = day.reportData;
      const clientData = day.clientData;
      if (!data || !clientData) return;

      const hasMeaningfulMacros =
        data.proteins > 0 || data.carbs > 0 || data.fats > 0;
      if (hasMeaningfulMacros) {
        daysWithMacros++;

        // Calculate goal adherence if goals are set
        const hasGoals =
          clientData.proteins > 0 ||
          clientData.carbs > 0 ||
          clientData.fats > 0;
        if (hasGoals) {
          let dayAdherence = 0;
          let macroCount = 0;

          if (clientData.proteins > 0) {
            const proteinAdherence = Math.min(
              data.proteins / clientData.proteins,
              1.5,
            );
            const proteinScore =
              proteinAdherence >= 0.8 && proteinAdherence <= 1.2
                ? 1.0
                : proteinAdherence >= 0.6 && proteinAdherence <= 1.4
                  ? 0.8
                  : proteinAdherence >= 0.4 && proteinAdherence <= 1.6
                    ? 0.6
                    : proteinAdherence > 0
                      ? 0.4
                      : 0;
            dayAdherence += proteinScore;
            macroCount++;
          }

          if (clientData.carbs > 0) {
            const carbAdherence = Math.min(data.carbs / clientData.carbs, 1.5);
            const carbScore =
              carbAdherence >= 0.8 && carbAdherence <= 1.2
                ? 1.0
                : carbAdherence >= 0.6 && carbAdherence <= 1.4
                  ? 0.8
                  : carbAdherence >= 0.4 && carbAdherence <= 1.6
                    ? 0.6
                    : carbAdherence > 0
                      ? 0.4
                      : 0;
            dayAdherence += carbScore;
            macroCount++;
          }

          if (clientData.fats > 0) {
            const fatAdherence = Math.min(data.fats / clientData.fats, 1.5);
            const fatScore =
              fatAdherence >= 0.8 && fatAdherence <= 1.2
                ? 1.0
                : fatAdherence >= 0.6 && fatAdherence <= 1.4
                  ? 0.8
                  : fatAdherence >= 0.4 && fatAdherence <= 1.6
                    ? 0.6
                    : fatAdherence > 0
                      ? 0.4
                      : 0;
            dayAdherence += fatScore;
            macroCount++;
          }

          if (macroCount > 0) {
            totalGoalAdherence += dayAdherence / macroCount;
            goalAdherenceDays++;
          }
        }
      }

      if (data.water > 0) {
        daysWithWater++;
      }
    });

    const macroTrackingRate =
      healthData.length > 0 ? daysWithMacros / healthData.length : 0;
    const waterTrackingRate =
      healthData.length > 0 ? daysWithWater / healthData.length : 0;
    const consistencyRate = totalDays > 0 ? healthData.length / totalDays : 0;
    const goalAdherenceRate =
      goalAdherenceDays > 0 ? totalGoalAdherence / goalAdherenceDays : 0;

    const nutritionScore =
      (goalAdherenceRate * 0.4 +
        macroTrackingRate * 0.3 +
        waterTrackingRate * 0.15 +
        consistencyRate * 0.15) *
      100;
    totalScore += nutritionScore * 0.3;
    totalWeight += 0.3;
  }

  // Daily check-ins (20% weight)
  if (healthData.length > 0) {
    let totalOptionalFields = 0;
    let completedOptionalFields = 0;

    healthData.forEach((day) => {
      const optionalData = day.reportData?.optional || {};
      Object.values(optionalData).forEach((field) => {
        totalOptionalFields++;
        // Use isQuestionAnswered to check if the question was explicitly answered
        if (isQuestionAnswered(field)) {
          completedOptionalFields++;
        }
      });
    });

    const completionRate =
      totalOptionalFields > 0
        ? completedOptionalFields / totalOptionalFields
        : 0;
    const checkInScore = completionRate * 100;
    totalScore += checkInScore * 0.2;
    totalWeight += 0.2;
  }

  // Weekly questions (20% weight)
  const weeklyOptionals = report.weeklyOptionals || [];
  const extraData = report.extraData?.optionals || {};
  if (weeklyOptionals.length > 0) {
    const answeredQuestions = weeklyOptionals.filter((q) => {
      const answer = extraData[q.id];
      // Use isQuestionAnswered to check if the question was explicitly answered
      return answer && isQuestionAnswered(answer);
    }).length;
    const weeklyScore = (answeredQuestions / weeklyOptionals.length) * 100;
    totalScore += weeklyScore * 0.2;
    totalWeight += 0.2;
  }

  // Supplements (5% weight)
  const supplementData = report.supplementData || [];
  const prescriptions = report.clientData?.supplementPrescriptions || [];
  if (prescriptions.length > 0) {
    const trackingDays = supplementData.length;
    const trackingRate = trackingDays / totalDays;
    const supplementScore = Math.min(trackingRate * 1.2, 1.0) * 100;
    totalScore += supplementScore * 0.05;
    totalWeight += 0.05;
  }

  const overallScore = totalWeight > 0 ? totalScore / totalWeight : 0;

  // Convert to 1-5 rating
  let overallRating;
  if (overallScore >= 90) overallRating = 5;
  else if (overallScore >= 80) overallRating = 4;
  else if (overallScore >= 70) overallRating = 3;
  else if (overallScore >= 60) overallRating = 2;
  else overallRating = 1;

  return {
    overallScore: Math.round(overallScore),
    overallRating,
  };
};

/**
 * Calculate variance for statistical analysis
 */
const calculateVariance = (scores) => {
  if (scores.length < 2) return 0;

  const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  const squaredDifferences = scores.map((score) => Math.pow(score - mean, 2));
  return (
    squaredDifferences.reduce((sum, diff) => sum + diff, 0) / scores.length
  );
};

/**
 * Analyze longer-term trends using linear regression
 */
const calculateLongerTermTrend = (scores) => {
  if (scores.length < 3) return { direction: "stable", periods: 0 };

  // Simple linear regression to find trend slope
  const n = scores.length;
  const x = scores.map((_, index) => index);
  const y = scores.map((item) => item.score);

  const sumX = x.reduce((sum, val) => sum + val, 0);
  const sumY = y.reduce((sum, val) => sum + val, 0);
  const sumXY = x.reduce((sum, val, index) => sum + val * y[index], 0);
  const sumXX = x.reduce((sum, val) => sum + val * val, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);

  const direction =
    slope > 2 ? "improving" : slope < -2 ? "declining" : "stable";

  return {
    direction,
    periods: n,
    slope: Math.round(slope * 10) / 10,
  };
};

export default useReportComplianceTrend;
